/**
 * The weather where the boat actually is, for the Conditions card.
 *
 * Separate from the barb FIELD on purpose. The field is a grid over the visible
 * chart, fetched only while the weather overlay is up in Plotting, which is the
 * right shape for drawing arrows and the wrong shape for answering "what is it
 * doing here, now". Opening Conditions under way in Live mode used to load
 * nothing at all.
 *
 * So this asks for one point: the boat. Two calls, both to Open-Meteo, both
 * hourly for three days, reusing the same request builders and parsers the field
 * uses so there is one place that knows what we ask for and how it comes back.
 * One point is a small enough request that the cost of a second endpoint does not
 * matter, and it means the card is right even when nothing is drawn on the chart.
 *
 * Wind, gusts and pressure come off the ordinary forecast model (GFS, ECMWF or
 * ICON, whichever the skipper picked). Waves and swell come off the marine model,
 * which is not model-specific and covers open sea only: up a loch or in a marina
 * it returns nothing, and nothing is what we then say. A flat calm and no
 * forecast are different answers.
 */
import {
  buildWindUrl,
  parseWindField,
  type RealModelId,
  type RealWindField,
} from './realWeather';
import { buildMarineUrl, parseSeaField, seaStateAtTime, type RealSeaField } from './seaState';
import type { SeaStateSample } from './seaState';

type FetchLike = (
  url: string,
) => Promise<{ ok: boolean; status: number; json: () => Promise<unknown> }>;

/** Both fields for the one point, as fetched. Sliced by time at read. */
export interface LocalConditionFields {
  position: [number, number];
  wind: RealWindField;
  /** Null when the marine call failed; the wind half is still worth showing. */
  sea: RealSeaField | null;
}

/** What the card shows. Every optional field is genuinely absent, not zero. */
export interface LocalConditions {
  /** Direction the wind comes FROM, degrees true. */
  windFromDeg: number | null;
  windKn: number | null;
  gustKn: number | null;
  pressureHpa: number | null;
  /** Null where the marine model does not cover this water. */
  sea: SeaStateSample | null;
}

function nearestHourIndex(timesMs: number[], ms: number): number {
  let best = -1;
  let bestD = Infinity;
  for (let i = 0; i < timesMs.length; i++) {
    const d = Math.abs(timesMs[i] - ms);
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  return best;
}

/** Read both fields at one instant. Anything the model did not give us is null. */
export function localConditionsAt(
  fields: LocalConditionFields,
  ms: number,
): LocalConditions {
  const t = nearestHourIndex(fields.wind.timesMs, ms);
  const num = (a: number[][] | undefined): number | null => {
    if (t < 0) return null;
    const v = Number(a?.[0]?.[t]);
    return Number.isFinite(v) ? v : null;
  };
  return {
    windFromDeg: num(fields.wind.dirFromDeg),
    windKn: num(fields.wind.speedKn),
    gustKn: num(fields.wind.gustKn),
    pressureHpa: num(fields.wind.pressureHpa),
    sea: fields.sea ? seaStateAtTime(fields.sea, fields.position, ms) : null,
  };
}

/**
 * Fetch both models for one position.
 *
 * The marine call is allowed to fail on its own. Open-Meteo answers it with an
 * error for a point it has no marine grid for, and losing the wind, gusts and
 * pressure because there is no wave forecast for a marina would be a poor trade.
 */
export async function fetchLocalConditions(
  position: [number, number],
  modelId: RealModelId,
  fetchImpl: FetchLike = fetch as unknown as FetchLike,
): Promise<LocalConditionFields> {
  const points: [number, number][] = [position];

  // Gusts and pressure are asked for, but not at the cost of the wind. Open-Meteo
  // rejects the whole request when a model does not carry a variable, so asking
  // for all four at once means a model without gusts returns nothing at all
  // rather than returning the wind. Try the full set, and if it is refused fall
  // back to the two we cannot do without. That is the difference between a card
  // missing a line and a card with nothing on it.
  const EXTRAS = ['wind_gusts_10m', 'pressure_msl'];
  let wr = await fetchImpl(buildWindUrl(points, modelId, EXTRAS));
  if (!wr.ok) wr = await fetchImpl(buildWindUrl(points, modelId));
  if (!wr.ok) throw new Error(`Weather fetch failed (${wr.status})`);
  const wind = parseWindField(await wr.json(), points);

  let sea: RealSeaField | null = null;
  try {
    const sr = await fetchImpl(buildMarineUrl(points));
    if (sr.ok) sea = parseSeaField(await sr.json(), points);
  } catch {
    // No marine forecast here, or the call fell over. The card says so.
  }
  return { position, wind, sea };
}
